import { Input as PomodoroInput } from '@ifood/pomodoro-components';
import { SyncOutlined } from '@ifood/pomodoro-icons';
import { useField, useFormikContext } from 'formik';
import React, { Ref, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';

import { useShareForwardedRef } from 'hooks/shared-forward-ref';
import { FieldName } from 'types/form';
import { PropsOf } from 'types/jsx';

import { DEFAULT_COLOR, parseCssColor, sanitizeHex } from '../../utils';
import { ColorPreviewButton, Container, HiddenColorInput, HiddenInputContainer, ResetToDefaultButton } from './styles';

export type ColorInputProps = {
  name: FieldName<string>;
} & PropsOf<typeof PomodoroInput>;

export const ColorInput = forwardRef<HTMLInputElement, ColorInputProps>(function DefaultInput(
  { name, ...props }: ColorInputProps,
  ref: Ref<HTMLInputElement>
) {
  const sharedRef = useShareForwardedRef<HTMLInputElement>(ref);

  const formik = useFormikContext();

  const [{ value }, { error, touched, initialValue }, { setTouched, setValue }] = useField(name);
  const [displayValue, setDisplayValue] = useState<string>();

  useEffect(() => {
    if (value?.toLowerCase() !== displayValue?.toLowerCase()) {
      setDisplayValue(value);
    }
  }, [value]);

  // This useEffect is required, so we pick up the values
  // the user typed before the JS finishes loading
  useEffectOnce(() => {
    if (sharedRef.current?.value) {
      setValue(sanitizeHex(sharedRef.current.value));
      setDisplayValue(sanitizeHex(sharedRef.current.value));
      setTouched(true);
    }
  });

  const nativeInputRef = useRef<HTMLInputElement>(null);

  const handleClick = useCallback(() => {
    nativeInputRef.current?.click();
  }, []);

  const handleChange = useCallback(
    (event): void => {
      setTouched(true);
      setDisplayValue(event.target.value);
    },
    [setTouched, setDisplayValue]
  );

  const resetToDefault = useCallback(() => {
    setValue(initialValue);
    setDisplayValue(initialValue);
  }, [initialValue, setValue]);

  const handleNativeChange = useCallback(
    (event): void => {
      formik.handleChange(event);
      const newColor = parseCssColor(event.target.value);
      if (displayValue !== newColor) {
        setDisplayValue(newColor);
      }
    },
    [formik, setDisplayValue]
  );

  const handleBlur = useCallback((): void => {
    setTouched(true);
    nativeInputRef.current?.blur();
    if (displayValue) {
      const actualValue = sanitizeHex(displayValue);
      setValue(actualValue);
      setDisplayValue(actualValue);
    } else {
      setValue('');
    }
  }, [setTouched, setDisplayValue, setValue]);

  const canReset = value !== initialValue;

  return (
    <Container>
      <PomodoroInput
        prefixComponent={
          <HiddenInputContainer>
            <HiddenColorInput
              name={name}
              onBlur={formik.handleBlur}
              onChange={handleNativeChange}
              value={value || ''}
              aria-hidden={true}
              ref={nativeInputRef}
              type="color"
            />
          </HiddenInputContainer>
        }
        suffixComponent={
          <>
            {canReset && (
              <ResetToDefaultButton type="button" variant="tertiary" onClick={resetToDefault}>
                <SyncOutlined />
              </ResetToDefaultButton>
            )}
            <ColorPreviewButton
              type="button"
              variant="tertiary"
              onClick={handleClick}
              style={{ backgroundColor: displayValue ? sanitizeHex(displayValue) : DEFAULT_COLOR }}
            />
          </>
        }
        ref={sharedRef}
        error={touched ? error : undefined}
        value={displayValue || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        {...props}
      />
    </Container>
  );
});
